// "Creá tu cuenta" (documento 20, decisión D51): el correo y una contraseña de 8 caracteres o más (sin otras reglas).
// Después, los datos del negocio ("Completá los datos de tu negocio"). Con Google, Apple o Facebook la cuenta se crea
// en el mismo paso, sin contraseña. Con el registro cerrado no se crean cuentas: la distribuidora da el acceso.
import { Icono } from '@reparto/ui';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { CLAVE_MINIMA } from '../compartido';
import { Aviso, Boton } from '../componentes/base';
import { Bienvenida } from '../componentes/Bienvenida';
import { CampoClave, CampoTexto } from '../componentes/Campos';
import { EntrarCon, Separador } from '../componentes/EntrarCon';
import { ErrorApi, mensajeDeError } from '../datos/api';
import { crearCuenta } from '../datos/cuenta';
import { aceptaRegistros, correoLimpio, errorClaveNueva, errorCorreo, proveedoresDe } from '../datos/entrada';
import { useMarca } from '../datos/marca';
import { leerVolver, olvidarVolver } from '../datos/volver';
import { ArribaEntrada, type EstadoEntrar } from './Entrar';
import { EnlacePrivacidad } from './Privacidad';

export function CrearCuenta() {
  const marca = useMarca((s) => s.marca);
  const navegar = useNavigate();
  const { state } = useLocation() as { state: EstadoEntrar | null };
  const paraConfirmar = !!state?.paraConfirmar;
  useEffect(() => {
    if (!paraConfirmar) olvidarVolver();
  }, [paraConfirmar]);
  const [email, setEmail] = useState(state?.email ?? '');
  const [clave, setClave] = useState('');
  const [errores, setErrores] = useState<{ email?: string | null; clave?: string | null }>({});
  const [error, setError] = useState<string | null>(null);
  const [enUso, setEnUso] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const acceso = marca?.acceso;
  const proveedores = proveedoresDe(acceso);
  const seguir = { paraConfirmar, email: correoLimpio(email) || undefined } satisfies EstadoEntrar;

  if (marca && !aceptaRegistros(acceso)) return <Navigate to="/entrar" replace state={state} />;

  async function crear(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    setEnUso(false);
    const e = { email: errorCorreo(email), clave: errorClaveNueva(clave) };
    setErrores(e);
    if (e.email || e.clave) return;
    setOcupado(true);
    try {
      await crearCuenta(correoLimpio(email), clave);
      // Ahora, los datos del negocio; después vuelve a donde iba (el carrito, si venía a confirmar).
      navegar('/registro', { replace: true });
    } catch (x) {
      setOcupado(false);
      if (x instanceof ErrorApi && x.codigo === 'CORREO_EN_USO') {
        setEnUso(true);
        setErrores({ email: 'Ese correo ya tiene una cuenta.' });
      } else setError(mensajeDeError(x));
    }
  }

  return (
    <Bienvenida marca={marca} lema="Creá tu cuenta para hacer tus pedidos">
      <ArribaEntrada paraConfirmar={paraConfirmar} />
      <form onSubmit={crear} noValidate className="flex flex-col gap-4">
        <div>
          <h2 className="text-2xl font-bold">Creá tu cuenta</h2>
          <p className="mt-0.5 text-stone-600">
            Tu correo y una contraseña. Después completás los datos de tu negocio, una sola vez.
          </p>
        </div>
        <CampoTexto
          etiqueta="Correo"
          icono="mail"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          placeholder="nombre@gmail.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (errores.email) setErrores((x) => ({ ...x, email: null }));
          }}
          error={errores.email}
        />
        {enUso ? (
          <Aviso tono="info" icono="person">
            Si es tuya,{' '}
            <Link to="/entrar" state={seguir} className="font-semibold underline underline-offset-2">
              entrá con tu contraseña
            </Link>{' '}
            o tocá{' '}
            <Link to="/olvide" state={seguir} className="font-semibold underline underline-offset-2">
              ¿Olvidaste tu contraseña?
            </Link>
          </Aviso>
        ) : null}
        <CampoClave
          etiqueta="Contraseña"
          ayuda={`${CLAVE_MINIMA} caracteres o más.`}
          autoComplete="new-password"
          value={clave}
          onChange={(e) => {
            setClave(e.target.value);
            if (errores.clave) setErrores((x) => ({ ...x, clave: null }));
          }}
          error={errores.clave}
        />
        {error ? (
          <Aviso tono="peligro" icono="error">
            <span role="alert">{error}</span>
          </Aviso>
        ) : null}
        <Boton type="submit" bloque tamano="lg" icono="person_add" cargando={ocupado} className="text-lg!">
          Crear cuenta
        </Boton>
      </form>

      {proveedores.length ? (
        <>
          <Separador className="my-4">o creala con</Separador>
          <EntrarCon proveedores={proveedores} volver={leerVolver()?.ruta ?? '/'} />
        </>
      ) : null}

      <div className="mt-8 border-t border-stone-200 pt-6 text-center">
        <p className="text-stone-700">¿Ya tenés cuenta?</p>
        <Link
          to="/entrar"
          state={seguir}
          className="mt-2 flex min-h-14 items-center justify-center gap-2 rounded-full border-2 border-primary-600 px-5 text-lg font-semibold text-primary-700 hover:bg-primary-50"
        >
          <Icono nombre="login" />
          Entrá a tu cuenta
        </Link>
      </div>
      <p className="mt-8 text-center">
        <EnlacePrivacidad />
      </p>
    </Bienvenida>
  );
}
